import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRefreshOnReconnect } from "@/hooks/use-backend-connectivity";
import {
  isScheduleRunsViewConfig,
  normalizeAgentViewSummary,
  type AgentViewSummary,
} from "@/api/views";
import { fetchWithAuth } from "@/lib/utils";
import { cacheScope, clearCacheOnAuthFailure, invalidateCache, readCache, writeCache } from "@/lib/ui-cache";
import { agentPreview } from "@/lib/ui-cache-shapes";
import { useCacheScope } from "@/hooks/use-cache-scope";

export interface Agent {
  id: string;
  name: string;
  description?: string;
  /** Optional model label from newer accessible-agent list responses. */
  model?: string;
  /** Optional effective access roles from newer accessible-agent list responses. */
  roles?: string[];
  type: "agent" | "team";
  /** Display tier for the agent picker.  "primary" agents are shown
   * directly; "secondary" agents are folded behind a "More agents"
   * collapsible.  "primary" is the default if the field is absent. */
  tier?: "primary" | "secondary";
  /** Sort order within the agent's tier.  Lower values appear first. */
  order?: number;
  /** Additive catalog hint for one-time sidebar pin bootstrap. Undefined from
   * older agno-server deployments means this agent has no marker. */
  pinnedByDefault?: boolean;
  /** Additive server diagnostic for a declaration that failed to reload.
   * Older agno-server deployments omit it. */
  configError?: string;
  /** Invoke roles for restricted agents. Missing means unrestricted, including
   * when connected to an older agno-server that does not expose this field. */
  allowed_roles?: string[];
  /** Lightweight, RBAC-filtered view tab metadata for this agent. */
  views?: AgentViewSummary[];
  actions?: ComposerAction[];
}

export interface ComposerAction {
  id: string;
  label: string;
  icon: string | null;
  confirm: boolean;
  workflow: string;
  order: number;
}

function isComposerAction(value: unknown): value is ComposerAction {
  if (!value || typeof value !== "object") return false;
  const action = value as Record<string, unknown>;
  return typeof action.id === "string" && typeof action.label === "string"
    && (action.icon === null || typeof action.icon === "string")
    && typeof action.confirm === "boolean" && typeof action.workflow === "string"
    && typeof action.order === "number";
}

export type AgentRefreshMode = "foreground" | "background";

export interface UseAgentsReturn {
  agents: Agent[];
  previewAgents: Agent[];
  cacheState: "none" | "cached-revalidating" | "stale" | "live";
  loading: boolean;
  error: string | null;
  /** True after a successful RBAC-filtered accessible-agent response. */
  agentCatalogLoaded: boolean;
  /** Timestamp of the most recent successful accessible-agent response. */
  agentCatalogUpdatedAt: number | null;
  /** Accessible-agent failures kept separate from the legacy sidebar error. */
  agentCatalogError: string | null;
  refresh: (mode?: AgentRefreshMode) => Promise<Agent[] | null>;
  findById: (id: string | null) => Agent | undefined;
}

// Agents: fetched from the RBAC-filtered /api/accessible-agents endpoint,
// which returns only the agents the caller may invoke (filtered by per-agent
// allowed_roles on the Agno server).  This keeps the agent picker consistent
// with the invoke gate — an agent that 404s on POST /agents/{id}/runs never
// appears in the picker.  Teams are NOT filtered (no per-team RBAC yet), so
// they still come from the unfiltered /api/teams proxy.
const AGENTS_URL = "/api/accessible-agents";
const TEAMS_URL = "/api/teams";

/**
 * Keep picker opens responsive while still picking up hot-reloaded tenant
 * agents shortly after they become available on the server.
 */
export const AGENT_CATALOG_BACKGROUND_STALE_MS = 12_000;

function isAgentViewSummary(view: unknown): view is AgentViewSummary {
  if (!view || typeof view !== "object") return false;
  const item = view as Record<string, unknown>;
  const isKnownType = (
    item.type === "sqlite_table"
    || item.type === "wiki"
    || item.type === "file_browser"
    || item.type === "single_file"
    || item.type === "code_preview_sidebar"
    || item.type === "visual_editor"
    || item.type === "dashboard"
    || item.type === "workflow"
    || item.type === "kanban_board"
    || item.type === "schedule_runs"
  );
  const viewConfig = item.config;
  const hasWorkflowConfig = Boolean(
    viewConfig
    && typeof viewConfig === "object"
    && typeof (viewConfig as Record<string, unknown>).file_name === "string"
  );
  const hasKanbanBoardConfig = Boolean(
    viewConfig
    && typeof viewConfig === "object"
      && typeof (viewConfig as Record<string, unknown>).project_id === "string"
  );
  // Dashboard project identity, roots, and capabilities are deliberately
  // absent from accessible-agent summaries. Bootstrap resolves them only
  // after the selected view is authorized by the narrow bridge route.
  const dashboardHasPrivateFields = item.type === "dashboard" && (
    [
      "config",
      "project_id",
      "root",
      "token",
      "static_url",
      "jwt",
      "expires_at",
      "bridge_origin",
      "api_base",
    ].some((key) => Object.prototype.hasOwnProperty.call(item, key))
    || (
      Object.prototype.hasOwnProperty.call(item, "initial_path")
      && item.initial_path !== null
    )
  );
  return (
    typeof item.id === "string" && item.id.trim().length > 0
    && typeof item.title === "string" && item.title.trim().length > 0
    && isKnownType
    && typeof item.icon === "string"
    && typeof item.order === "number"
    && (item.type !== "workflow" || hasWorkflowConfig)
    && (item.type !== "kanban_board" || hasKanbanBoardConfig)
    && (item.type !== "schedule_runs" || isScheduleRunsViewConfig(viewConfig))
    && !dashboardHasPrivateFields
  );
}

function allowedRolesFromResponse(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const roles = value.filter(
    (role): role is string => typeof role === "string" && role.trim().length > 0,
  );
  return roles.length > 0 ? roles : undefined;
}

function nonEmptyString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function stringArray(value: unknown): string[] | undefined {
  if (!Array.isArray(value)) return undefined;

  const values: string[] = [];
  for (const item of value) {
    const text = nonEmptyString(item);
    if (text) values.push(text);
  }
  return [...new Set(values)];
}

function modelLabel(value: unknown): string | undefined {
  const direct = nonEmptyString(value);
  if (direct) return direct;
  if (!value || typeof value !== "object") return undefined;

  const model = value as Record<string, unknown>;
  return nonEmptyString(model.slug) ?? nonEmptyString(model.id) ?? nonEmptyString(model.name);
}

/**
 * Newer accessible-agent directories may expose a model and effective roles.
 * Read a few safe aliases so independently deployed bridge/server versions
 * can roll out those optional fields without changing the picker contract.
 */
function agentModel(item: unknown): string | undefined {
  if (!item || typeof item !== "object") return undefined;
  const agent = item as Record<string, unknown>;
  const config = agent.config && typeof agent.config === "object"
    ? agent.config as Record<string, unknown>
    : undefined;

  return (
    modelLabel(agent.model)
    ?? modelLabel(agent.model_slug)
    ?? modelLabel(config?.model)
  );
}

function agentRoles(item: unknown): string[] | undefined {
  if (!item || typeof item !== "object") return undefined;
  const agent = item as Record<string, unknown>;
  const config = agent.config && typeof agent.config === "object"
    ? agent.config as Record<string, unknown>
    : undefined;
  const rbac = agent.rbac && typeof agent.rbac === "object"
    ? agent.rbac as Record<string, unknown>
    : undefined;
  const invoke = rbac?.invoke && typeof rbac.invoke === "object"
    ? rbac.invoke as Record<string, unknown>
    : undefined;

  return (
    stringArray(agent.allowed_roles)
    ?? stringArray(agent.roles)
    ?? stringArray(invoke?.allowed_roles)
    ?? stringArray(config?.allowed_roles)
  );
}

/**
 * Newer accessible-agent directories expose this as an additive snake-case
 * presentation hint. Invalid and older values are intentionally ignored.
 */
function agentPinnedByDefault(item: unknown): boolean | undefined {
  if (!item || typeof item !== "object") return undefined;
  const agent = item as Record<string, unknown>;
  return typeof agent.pinned_by_default === "boolean"
    ? agent.pinned_by_default
    : undefined;
}

function agentConfigError(item: unknown): string | undefined {
  if (!item || typeof item !== "object") return undefined;
  return nonEmptyString((item as Record<string, unknown>).config_error);
}

export function useAgents(getToken: () => Promise<string>): UseAgentsReturn {
  const verifiedScope = useCacheScope();
  const previousScopeRef = useRef(verifiedScope);
  const [cacheState, setCacheState] = useState<UseAgentsReturn["cacheState"]>("none");
  const { t } = useTranslation("chat");
  const [agents, setAgents] = useState<Agent[]>([]);
  const [previewAgents, setPreviewAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agentCatalogLoaded, setAgentCatalogLoaded] = useState(false);
  const [agentCatalogUpdatedAt, setAgentCatalogUpdatedAt] = useState<number | null>(null);
  const [agentCatalogError, setAgentCatalogError] = useState<string | null>(null);
  const refreshInFlightRef = useRef<Promise<Agent[] | null> | null>(null);
  const agentCatalogLoadedRef = useRef(false);
  const agentCatalogUpdatedAtRef = useRef<number | null>(null);

  const refresh = useCallback((mode: AgentRefreshMode = "foreground"): Promise<Agent[] | null> => {
    if (refreshInFlightRef.current) return refreshInFlightRef.current;

    const hasLoadedCatalog = agentCatalogLoadedRef.current;
    const preserveLoadedCatalog = mode === "background" && hasLoadedCatalog;
    const updatedAt = agentCatalogUpdatedAtRef.current;
    if (
      preserveLoadedCatalog
      && updatedAt !== null
      && Date.now() - updatedAt < AGENT_CATALOG_BACKGROUND_STALE_MS
    ) {
      return Promise.resolve(null);
    }

    const request = (async (): Promise<Agent[] | null> => {
      const requestScope = cacheScope();
      // A loaded picker must stay mounted while its catalog is revalidated.
      // The old list remains usable until the complete replacement is ready.
      if (!preserveLoadedCatalog) {
        setLoading(true);
        setError(null);
        setAgentCatalogError(null);
      }
      try {
        const [agentsRes, teamsRes] = await Promise.all([
          fetchWithAuth(AGENTS_URL, { headers: {} }, getToken).catch(() => null),
          fetchWithAuth(TEAMS_URL, { headers: {} }, getToken).catch(() => null),
        ]);
        if (requestScope !== cacheScope()) return null;

        const items: Agent[] = [];
        let accessibleAgentsAvailable = false;
        let accessibleAgentsError: string | null = null;
        let teamsError: string | null = null;

        if (agentsRes?.ok) {
          try {
            const data = await agentsRes.json() as unknown;
            const body = data && typeof data === "object"
              ? data as { agents?: unknown }
              : null;
            const arr = Array.isArray(data)
              ? data
              : body && Array.isArray(body.agents)
                ? body.agents
                : null;
            if (!arr) throw new Error(t("agent.errors.invalidResponse"));
            for (const a of arr) {
              const pinnedByDefault = agentPinnedByDefault(a);
              const configError = agentConfigError(a);
              items.push({
                id: a.id,
                name: a.name || a.id,
                description: a.description,
                model: agentModel(a),
                roles: agentRoles(a),
                type: "agent",
                tier: a.tier === "secondary" ? "secondary" : "primary",
                order: typeof a.order === "number" ? a.order : 100,
                ...(pinnedByDefault === undefined ? {} : { pinnedByDefault }),
                ...(configError === undefined ? {} : { configError }),
                allowed_roles: allowedRolesFromResponse(a.allowed_roles),
                views: Array.isArray(a.views)
                  ? a.views
                      .filter(isAgentViewSummary)
                      .map((view: AgentViewSummary & { is_default?: unknown }) => {
                        const normalized = normalizeAgentViewSummary(view);
                        if (view.type !== "file_browser") return normalized;
                        const mount = view.mount_id;
                        if (typeof mount !== "string" || !mount.trim() || mount.length > 80
                          || /[\u0000-\u001f\u007f]/.test(mount)
                          || view.mount_contract_version !== 1) {
                          const { mount_id: _mount, mount_contract_version: _version, ...safe } = normalized;
                          return safe;
                        }
                        return normalized;
                      })
                      .sort(
                        (left: AgentViewSummary, right: AgentViewSummary) =>
                          left.order - right.order || left.id.localeCompare(right.id),
                      )
                  : [],
                actions: Array.isArray(a.actions)
                  ? a.actions.filter(isComposerAction).sort((left: ComposerAction, right: ComposerAction) => left.order - right.order)
                  : [],
              });
            }
            accessibleAgentsAvailable = true;
          } catch (err: any) {
            accessibleAgentsError = err?.message ?? t("agent.errors.loadAccessible" );
          }
        } else {
          if (agentsRes?.status === 401 || agentsRes?.status === 403) {
            clearCacheOnAuthFailure();
            setAgents([]);
            setPreviewAgents([]);
            agentCatalogLoadedRef.current = false;
            setAgentCatalogLoaded(false);
            setCacheState("none");
            void invalidateCache("agentCatalogs");
          }
          accessibleAgentsError = agentsRes
            ? t("agent.errors.unavailableStatus", { status: agentsRes.status })
            : t("agent.errors.unavailable");
        }

        if (teamsRes && teamsRes.ok) {
          try {
            const data = await teamsRes.json();
            const arr = Array.isArray(data) ? data : data.teams ?? [];
            for (const t of arr) {
              items.push({
                id: t.id,
                name: t.name || t.id,
                description: t.description,
                type: "team",
              });
            }
          } catch (err: any) {
            // Teams are sidebar-only. A malformed teams response must not
            // make the RBAC-filtered agent picker unavailable.
            teamsError = err?.message ?? t("agent.errors.loadTeams");
          }
        }

        // Keep sidebar updates atomic: a background fetch replaces the old
        // catalog only after both response bodies have been processed. A
        // failed accessible-agent response must not clear an already visible
        // RBAC-filtered list while a retry is pending.
        if (accessibleAgentsAvailable || !agentCatalogLoadedRef.current) setAgents(items);

        if (accessibleAgentsAvailable) {
          setPreviewAgents([]);
          const preview = items.map(agentPreview).filter((item): item is Agent => !!item);
          void writeCache("agentCatalogs", "catalog", "accessible", preview);
          setCacheState("live");
          const refreshedAt = Date.now();
          agentCatalogLoadedRef.current = true;
          agentCatalogUpdatedAtRef.current = refreshedAt;
          setAgentCatalogLoaded(true);
          setAgentCatalogUpdatedAt(refreshedAt);
          setAgentCatalogError(null);
          if (!teamsError) setError(null);
        } else if (!preserveLoadedCatalog && accessibleAgentsError) {
          setCacheState((state) => state === "cached-revalidating" ? "stale" : state);
          // Older servers may not expose this endpoint. Keep the UI usable,
          // but surface the unsupported/unavailable state with its retry
          // affordance instead of silently rendering an empty agent section.
          setAgentCatalogError(accessibleAgentsError);
          if (!agentCatalogLoadedRef.current || agentsRes?.ok) {
            setError(accessibleAgentsError);
          }
        }

        if (teamsError && !preserveLoadedCatalog) setError(teamsError);
        return accessibleAgentsAvailable ? items : null;
      } catch (err: any) {
        setCacheState((state) => state === "cached-revalidating" ? "stale" : state);
        if (!preserveLoadedCatalog) {
          setError(err?.message ?? t("agent.errors.load"));
          setAgentCatalogError(err?.message ?? t("agent.errors.loadAccessible"));
        }
        return null;
      } finally {
        if (!preserveLoadedCatalog) setLoading(false);
      }
    })();

    refreshInFlightRef.current = request;
    void request.finally(() => {
      if (refreshInFlightRef.current === request) {
        refreshInFlightRef.current = null;
      }
    });
    return request;
  }, [getToken, t]);

  const findById = useCallback(
    (id: string | null) => agents.find((a) => a.id === id),
    [agents]
  );

  useRefreshOnReconnect(() => refresh("background"));

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (previousScopeRef.current === verifiedScope) return;
    previousScopeRef.current = verifiedScope;
    setAgents([]);
    setPreviewAgents([]);
    refreshInFlightRef.current = null;
    setAgentCatalogLoaded(false);
    agentCatalogLoadedRef.current = false;
    setCacheState("none");
    if (!verifiedScope) return;
    let cancelled = false;
    void readCache<Agent[]>("agentCatalogs", "catalog", "accessible").then((preview) => {
      if (cancelled || cacheScope() !== verifiedScope || agentCatalogLoadedRef.current || !preview?.length) return;
      setPreviewAgents(preview.map(agentPreview).filter((item): item is Agent => !!item));
      setCacheState("cached-revalidating");
    });
    void refresh("foreground");
    return () => { cancelled = true; };
  }, [verifiedScope]);

  return {
    agents,
    previewAgents,
    cacheState,
    loading,
    error,
    agentCatalogLoaded,
    agentCatalogUpdatedAt,
    agentCatalogError,
    refresh,
    findById,
  };
}
